import { RefreshCwIcon } from "lucide-react"
import type * as React from "react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import { cn } from "@/lib/utils"

/**
 * "Synced 2 minutes ago" + Sync now, with a live badge while it happens.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY A SCREEN NEEDS THIS AT ALL. Everything here is cached (TanStack Query),
 * and a cache is invisible: the owner cannot tell a bill loaded a second ago
 * from one loaded before lunch. That matters most on the two screens where the
 * data genuinely moves under him — a bill his son just cancelled from the shop
 * computer, a draft he left open on the phone. Showing the age of what he is
 * looking at, and a way to refresh it, is the smallest honest answer.
 *
 * THE BADGE IS THE FEEDBACK, not a spinner replacing the content. Refetching
 * keeps the old data on screen (that is the point of a background refetch), so
 * blanking the page to a skeleton would be a lie about what is happening. The
 * badge says "working" beside data that is still perfectly readable.
 *
 * THE CLOCK TICKS. "Synced just now" that still says "just now" ten minutes
 * later is worse than no timestamp — so the label re-renders on a 30s interval.
 * The interval is cleared on unmount; there is no timer per row anywhere,
 * because this component only ever appears once per screen.
 *
 * TIME HERE IS AN INSTANT, NOT A CALENDAR DAY. Unlike document dates (see
 * `lib/dates.ts`, which forbids `Date` entirely), "when did we last fetch" is a
 * real moment in time and `Date` is the correct type for it. The two must not
 * be confused: this never touches a document date and never formats one.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** How often the "…ago" label refreshes itself. */
const TICK_MS = 30_000;

export function SyncStatus({
  /** `query.dataUpdatedAt` — 0 while nothing has ever loaded. */
  updatedAt,
  /** `query.isFetching` — true for a background refetch as well as a first load. */
  syncing,
  onSync,
  className,
}: {
  updatedAt: number
  syncing: boolean
  onSync: () => void
  className?: string
}): React.JSX.Element {
  const { t } = useTranslation()

  // Re-render on a timer so the relative label stays true without the parent
  // having to know anything about it.
  const [, setTick] = useState(0)
  useEffect(() => {
    const timer = window.setInterval(() => {
      setTick((current) => current + 1)
    }, TICK_MS)
    return () => {
      window.clearInterval(timer)
    }
  }, [])

  /*
   * "just now" / "5 min ago" / "at 14:20" — through `t()`, never a date
   * library. Anything older than an hour switches to a CLOCK TIME rather than
   * growing to "3 hours ago": at that age the exact moment is more useful than
   * the distance, and it spares the string plural rules in two languages
   * (§11.4).
   *
   * Computed inline rather than in a helper taking `t`: i18next's `TFunction`
   * is a heavily overloaded type, and hand-writing a narrower signature for it
   * only produces a cast that hides the real one.
   */
  const secondsAgo = updatedAt === 0 ? -1 : Math.max(0, Math.floor((Date.now() - updatedAt) / 1000))
  const minutesAgo = Math.floor(secondsAgo / 60)
  const label =
    secondsAgo < 0
      ? t("sync.never")
      : secondsAgo < 60
        ? t("sync.justNow")
        : minutesAgo < 60
          ? t("sync.minutesAgo", { count: minutesAgo })
          : t("sync.atTime", {
              time: new Date(updatedAt).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              }),
            })

  if (syncing) {
    return (
      /*
       * SIZED LIKE THE BUTTON IT REPLACES. A Badge is `h-5` with `size-3`
       * icons; the row it sits in is `h-8`, so the stock badge made the whole
       * header shrink and spring back every time a sync ran — motion that
       * reads as a glitch rather than as progress. Height, padding, radius and
       * text size are matched to the outline Button below.
       *
       * The `!` on the icon size is not decoration: the Badge sets
       * `[&>svg]:size-3!` with its own important flag, so a plain `size-4`
       * loses to it.
       */
      <Badge
        variant="outline"
        className={cn(
          "h-8 gap-1.5 rounded-lg px-2.5 text-sm font-medium [&>svg]:size-4!",
          className,
        )}
      >
        <Spinner data-icon="inline-start" />
        {t("sync.syncing")}
      </Badge>
    )
  }

  return (
    <div className={cn("flex items-center gap-1", className)}>
      <span className="text-muted-foreground text-xs whitespace-nowrap">{label}</span>
      {/*
        * DEFAULT SIZE, not `sm`. This sits in a row with "View the bill" and
        * the two menu triggers, all `h-8`; an `h-7` control among them reads as
        * an afterthought rather than as a peer, and is a smaller tap target in
        * a workshop for no reason. `outline` for the same reason the triggers
        * are outline — same kind of control, same weight.
        */}
      {/* No `aria-label`: the visible text IS the accessible name now that it
        * no longer collapses to an icon, and a duplicate label that drifts in
        * one locale is worse than none. */}
      <Button type="button" variant="outline" onClick={onSync}>
        <RefreshCwIcon />
        {t("sync.now")}
      </Button>
    </div>
  )
}
